// ArchiveSnapshotView (#2140) — read-only frozen render of a single return
//   snapshot. FR-4 hard rule: NO transition/edit/carrier-management/files/
//   chat/audit/duplicate/Russia controls — only the frozen header, the cargo
//   (items + boxes), the carriers/bids, and the back-to-live control. All
//   photo leaves resolve through the snapshot-scoped endpoint via the
//   injected `PhotoDownloadResolverProvider`.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrencyRates } from '@magimp/shared-hooks';
import { Button, EmptyState, ErrorState, IconButton, LoadingState } from '../../../../ui/index.js';
import { requestSnapshotPhotoDownloadUrl } from '../../../../api/requests/index.js';
import { formatDateTime } from '../../format.js';
import { formatRequestOrigin } from '../../origin-display.js';
import { useRequestSnapshot } from '../../queries.js';
import { snapshotPayloadToView } from '../../snapshotAdapter.js';
import { PhotoDownloadResolverProvider } from '../../photoDownloadResolver.js';
import type { RequestView, UserRef } from '../../types.js';
import { RequestStatusPill } from '../RequestStatusPill.js';
import { CarrierBidCard } from './CarrierBidCard.js';
import { ItemsTab } from './ItemsTab.js';

// WHY (#2140): `ItemsTab` requires a `me: UserRef` prop but never reads it for
//   rendering (read-only cargo). A minimal frozen ref satisfies the contract.
const DUMMY_ME: UserRef = { id: '', name: '', role: 'manager' };

const NOOP = () => undefined;

export function ArchiveSnapshotView({
  request,
  snapshotId,
  onBack,
  onClose,
}: {
  request: RequestView;
  snapshotId: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const snapshotQuery = useRequestSnapshot(request.id, snapshotId);
  const { data: fx } = useCurrencyRates();
  // WHY (#2140): resolve frozen photo objectKeys through the snapshot-scoped
  //   endpoint (live item/box keys may have been edited after the return).
  const resolver = useCallback(
    (objectKey: string) =>
      requestSnapshotPhotoDownloadUrl(request.id, snapshotId, objectKey)
        .then((r) => r.downloadUrl)
        .catch(() => null),
    [request.id, snapshotId],
  );

  if (snapshotQuery.isLoading) return <LoadingState />;
  if (snapshotQuery.isError || !snapshotQuery.data) {
    return (
      <ErrorState
        text={t('requests:archive.loadError')}
        onRetry={() => void snapshotQuery.refetch()}
      />
    );
  }

  const detail = snapshotQuery.data;
  const view = snapshotPayloadToView(detail.payload);
  const fxRates = fx?.rates ?? {};

  return (
    <PhotoDownloadResolverProvider resolver={resolver}>
      <div
        data-testid="request-archive-snapshot-scroll"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '12px 24px',
            borderBottom: '1px solid var(--border-2)',
          }}
        >
          {/* WHY (#2140): the snapshot takeover hides the live drawer header,
              so it must carry both the back-to-live control AND the top-right
              close affordance that header normally provides. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <Button
              kind="ghost"
              size="sm"
              icon="chevron-left"
              onClick={onBack}
              data-testid="request-archive-back"
            >
              {t('requests:archive.backToRequest')}
            </Button>
            <IconButton icon="x" label={t('common:actions.close')} onClick={onClose} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
              {t('requests:archive.snapshotTitle')}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)' }}>
              {view.id}
            </span>
            <RequestStatusPill status={view.status} size="sm" />
          </div>
          <span style={{ fontSize: 13, color: 'var(--fg-1)', overflowWrap: 'anywhere' }}>
            {view.name}
          </span>
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
            {formatRequestOrigin(view, t('requests:draftDefaults.from'))} → {view.route.to}
          </span>
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
            {t('requests:archive.returnedAt', { date: formatDateTime(detail.returnedAt) })}
          </span>
          {detail.reason && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--fg-2)',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
              }}
            >
              {detail.reason}
            </span>
          )}
        </div>

        <ItemsTab request={view} me={DUMMY_ME} />

        <section style={{ padding: '12px 24px 24px', display: 'grid', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
            {t('requests:tabs.carriers', { count: view.carriers.length })}
          </h3>
          {view.carriers.length === 0 ? (
            <EmptyState icon="ship" title={t('requests:archive.carriersEmpty')} />
          ) : (
            <ul
              data-testid="request-archive-carriers"
              style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}
            >
              {view.carriers.map((c) => (
                <li key={c.id}>
                  <CarrierBidCard
                    bid={c}
                    requestId={view.id}
                    request={view}
                    winnerBidId={null}
                    cheapestBidId={null}
                    canManage={false}
                    canRevive={false}
                    canPick={false}
                    fxRates={fxRates}
                    onAddBid={NOOP}
                    onEditBid={NOOP}
                    onDeleteBid={NOOP}
                    onReject={NOOP}
                    onReset={NOOP}
                    onDelete={NOOP}
                    onPickBid={NOOP}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </PhotoDownloadResolverProvider>
  );
}
